import Button from "devextreme-react/button";
import TagBox from "devextreme-react/tag-box";
import { confirm } from "devextreme/ui/dialog";
import React, { useState, useEffect, useMemo } from "react";
import { DefaultPhaseDto } from "../../../dtos/DefaultPhaseDto";
import { DefaultStepDto } from "../../../dtos/DefaultStepDto";
import { WorkflowConfigOptionsDto } from "../../../dtos/WorkflowConfigOptionsDto";
import { UserRole } from "../../../dtos/UserRole";
import { StepStatus } from "../../../dtos/StepStatus";
import { groupRoleOptions } from "../../shared/tasklist/config/audienceConfig";
import AddRowsDialog from "./components/AddRowsDialog";
import TemplateGroupBox, { removedTextStyle } from "./components/TemplateGroupBox";
import useExpandedGroups from "./hooks/useExpandedGroups";
import useStagedRows from "./hooks/useStagedRows";

// Mode determines what transitions we're editing:
// - "subtask": Subtask's own transitions (subtask.settings.workflow.transitions)
// - "defaultSubtask": Task's default subtask transitions (task.settings.defaultStepSettings.workflow.transitions)
export type TransitionsMode = "subtask" | "defaultSubtask";

type Props = {
    task?: DefaultPhaseDto | null;
    subtask?: DefaultStepDto | null;
    configOptions: WorkflowConfigOptionsDto;
    onSave: (data: DefaultPhaseDto | DefaultStepDto) => Promise<void>;
    mode?: TransitionsMode; // Defaults to "subtask" if subtask provided
};

type TransitionEntry = {
    from: StepStatus;
    to: StepStatus;
    roles: UserRole[];
};

export default function TemplateTransitionsTab({ task, subtask, configOptions, onSave, mode }: Props) {
    // Determine effective mode
    const effectiveMode: TransitionsMode = mode ?? "subtask";
    const isSubtaskMode = effectiveMode === "subtask";
    const isDefaultSubtaskMode = effectiveMode === "defaultSubtask";

    const transitionOptions = configOptions.subtask.transitions;
    const roleOptions = configOptions.roles.filter(r => !r.notificationOnly);
    const groupedRoles = useMemo(() => groupRoleOptions(roleOptions), [roleOptions]);

    // Get current transitions config based on mode
    const currentTransitions = isDefaultSubtaskMode
        ? (task?.settings?.defaultStepSettings?.workflow?.transitions?.transitions ?? [])
        : (subtask?.settings?.workflow?.transitions?.transitions ?? []);

    // Build initial state
    const buildInitialState = (): TransitionEntry[] => {
        return transitionOptions.map(opt => {
            const existing = currentTransitions.find(
                t => t.from === opt.from && t.to === opt.to
            );
            return {
                from: opt.from,
                to: opt.to,
                roles: existing?.roles ?? []
            };
        });
    };

    const [transitions, setTransitions] = useState<TransitionEntry[]>(buildInitialState);
    const [savedTransitions, setSavedTransitions] = useState<TransitionEntry[]>(buildInitialState);

    // Reset state when task/subtask changes
    useEffect(() => {
        const initial = buildInitialState();
        setTransitions(initial);
        setSavedTransitions(initial);
        staged.clear();
    }, [task?.id, subtask?.id, effectiveMode]);

    // Compute dirty state
    const updateTransitionRoles = (from: StepStatus, to: StepStatus, newRoles: UserRole[]) => {
        setTransitions(prev =>
            prev.map(t =>
                t.from === from && t.to === to
                    ? { ...t, roles: newRoles }
                    : t
            )
        );
    };

    const getRolesForTransition = (from: StepStatus, to: StepStatus): UserRole[] => {
        const entry = transitions.find(t => t.from === from && t.to === to);
        return entry?.roles ?? [];
    };

    // Restore defaults from the backend config
    const handleRestoreDefaults = () => {
        const defaultConfig = configOptions.subtask.defaultTransitions;

        setTransitions(transitionOptions.map(opt => {
            const existing = defaultConfig?.transitions?.find(
                t => t.from === opt.from && t.to === opt.to
            );
            return {
                from: opt.from,
                to: opt.to,
                roles: existing?.roles ?? []
            };
        }));
    };

    const handleSave = async () => {
        // Filter to only transitions with roles assigned
        const transitionsWithRoles = keptTransitions.filter(t => t.roles.length > 0);

        if (isDefaultSubtaskMode && task) {
            // Task's default subtask transitions
            const currentDefaults = task.settings?.defaultStepSettings;
            const updatedTask: DefaultPhaseDto = {
                ...task,
                settings: {
                    ...task.settings,
                    defaultStepSettings: {
                        unrestrictedUpload: currentDefaults?.unrestrictedUpload ?? false,
                        fileRestrictions: currentDefaults?.fileRestrictions ?? null,
                        fileLimit: currentDefaults?.fileLimit ?? null,
                        allowedExtensions: currentDefaults?.allowedExtensions ?? null,
                        offsetStart: currentDefaults?.offsetStart ?? null,
                        offsetEnd: currentDefaults?.offsetEnd ?? null,
                        dueDate: currentDefaults?.dueDate ?? null,
                        requestFileAttachments: currentDefaults?.requestFileAttachments ?? null,
                        triggerActions: currentDefaults?.triggerActions ?? null,
                        initialStepStatus: currentDefaults?.initialStepStatus ?? null,
                        notifications: currentDefaults?.notifications ?? null,
                        workflow: {
                            permissions: currentDefaults?.workflow?.permissions ?? null,
                            transitions: {
                                transitions: transitionsWithRoles
                            }
                        },
                        workflowType: currentDefaults?.workflowType ?? null,
                        isCore: currentDefaults?.isCore ?? false,
                    }
                }
            };
            await onSave(updatedTask);
        } else if (isSubtaskMode && subtask) {
            // Subtask's own transitions
            const updatedSubtask: DefaultStepDto = {
                ...subtask,
                settings: {
                    ...subtask.settings,
                    workflow: {
                        permissions: subtask.settings?.workflow?.permissions ?? null,
                        transitions: {
                            transitions: transitionsWithRoles
                        }
                    }
                }
            };
            await onSave(updatedSubtask);
        }
        setSavedTransitions(keptTransitions);
        setTransitions(keptTransitions);
        staged.clear();
    };

    const handleDiscard = () => {
        setTransitions(savedTransitions);
        staged.clear();
    };

    // One click empties every transition, so it is worth asking first
    const handleClearAll = async () => {
        const confirmed = await confirm("Remove the roles from every transition on this tab?", "Clear All");
        if (!confirmed) return;
        setTransitions(prev => prev.map(t => ({ ...t, roles: [] })));
        staged.clear();
    };

    // Group transitions by label for grouped display
    const groupedTransitions = useMemo(() => {
        const groups: { label: string; options: typeof transitionOptions }[] = [];
        const labelOrder: string[] = [];

        for (const opt of transitionOptions) {
            if (!labelOrder.includes(opt.label)) {
                labelOrder.push(opt.label);
                groups.push({ label: opt.label, options: [] });
            }
            const group = groups.find(g => g.label === opt.label);
            if (group) {
                group.options.push(opt);
            }
        }

        return groups;
    }, [transitionOptions]);

    const groups = useExpandedGroups(groupedTransitions.map(g => g.label));
    const staged = useStagedRows();
    const [addForGroup, setAddForGroup] = useState<string | null>(null);

    const keptTransitions = useMemo(
        () => transitions.map(t => staged.isRemoved(`${t.from}-${t.to}`) ? { ...t, roles: [] } : t),
        [transitions, staged]);

    const isDirty = useMemo(() => {
        const toComparable = (transitionList: TransitionEntry[]) => {
            return transitionList
                .map(t => `${t.from}-${t.to}-[${[...t.roles].sort().join(",")}]`)
                .sort()
                .join(";");
        };
        return toComparable(keptTransitions) !== toComparable(savedTransitions);
    }, [keptTransitions, savedTransitions]);

    const rowKey = (opt: { from: StepStatus; to: StepStatus }) => `${opt.from}-${opt.to}`;

    // The transitions themselves are fixed; a row is on screen once it has roles, or once the user
    // has added it and not picked any yet - the same rule the permissions tab uses for a status.
    const rowsFor = (options: typeof transitionOptions) => options.filter(o =>
        getRolesForTransition(o.from, o.to).length > 0 || staged.isAdded(rowKey(o)));

    const groupSummary = (options: typeof transitionOptions) => {
        const kept = rowsFor(options).filter(o => !staged.isRemoved(rowKey(o))).length;
        if (kept === 0) return "none configured";
        return `${kept} ${kept === 1 ? "transition" : "transitions"} configured`;
    };

    // Adding opens the group, so what was just added is visible
    const addRows = (label: string, keys: string[], roles: UserRole[]) => {
        staged.addRows(keys);
        groups.expand(label);
        setTransitions(prev => prev.map(t => keys.includes(`${t.from}-${t.to}`) ? { ...t, roles } : t));
        setAddForGroup(null);
    };

    const descriptionText = isDefaultSubtaskMode
        ? "Configure subtask transitions. New subtasks will inherit these settings."
        : "Configure which roles can perform each status transition.";

    return (
        <div className="template-tab">
            <div className="template-tab-body">
            <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: "16px" }}>
                <p style={{ flex: 1, margin: 0, color: "#666" }}>
                    {descriptionText}
                </p>
                {groupedTransitions.length > 0 && (
                    <Button
                        icon={groups.allExpanded ? "chevronup" : "chevrondown"}
                        text={groups.allExpanded ? "Collapse All" : "Expand All"}
                        stylingMode="outlined"
                        onClick={groups.toggleAll}
                    />
                )}
            </div>

            <div style={{ display: "grid", gap: "16px" }}>
                {groupedTransitions.map(group => (
                    <TemplateGroupBox
                        key={group.label}
                        title={group.label}
                        summary={groupSummary(group.options)}
                        collapsed={!groups.isExpanded(group.label)}
                        onToggle={() => groups.toggle(group.label)}
                        testId="transition-group"
                        toggleTestId="transition-collapse-toggle"
                        headerRight={
                            <Button
                                icon="add"
                                text="Add Transition"
                                stylingMode="text"
                                disabled={rowsFor(group.options).length === group.options.length}
                                hint={rowsFor(group.options).length === group.options.length
                                    ? "Every transition for this action is configured"
                                    : undefined}
                                onClick={() => setAddForGroup(group.label)}
                            />
                        }
                    >
                        <div style={{ display: "grid", gap: "8px" }}>
                            {rowsFor(group.options).length === 0 && (
                                <p style={{ color: "#888", fontSize: "13px", margin: 0 }}>
                                    No transitions configured for this action.
                                </p>
                            )}
                            {rowsFor(group.options).map(opt => {
                                const removed = staged.isRemoved(rowKey(opt));
                                return (
                                    <div
                                        key={rowKey(opt)}
                                        data-testid="transition-row"
                                        data-removed={removed}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "12px"
                                        }}
                                    >
                                        <div style={{ width: "220px", minWidth: "220px" }}>
                                            <span style={{ fontSize: "13px", ...(removed ? removedTextStyle : { color: "#555" }) }}>
                                                {opt.description}
                                            </span>
                                        </div>
                                        <TagBox
                                            items={groupedRoles}
                                            displayExpr="name"
                                            valueExpr="id"
                                            grouped={true}
                                            value={getRolesForTransition(opt.from, opt.to)}
                                            onValueChanged={(e) => updateTransitionRoles(opt.from, opt.to, e.value ?? [])}
                                            width="100%"
                                            placeholder="Select roles..."
                                            showSelectionControls={true}
                                            readOnly={removed}
                                        />
                                        <Button
                                            icon={removed ? "undo" : "trash"}
                                            stylingMode="text"
                                            hint={removed ? `Keep ${opt.description}` : `Remove ${opt.description}`}
                                            onClick={() => removed ? staged.restoreRow(rowKey(opt)) : staged.removeRow(rowKey(opt))}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </TemplateGroupBox>
                ))}
            </div>

            </div>

            <div className="template-tab-footer">
                <Button
                    text={isDirty ? "Save Transitions *" : "Save Transitions"}
                    type="default"
                    stylingMode="contained"
                    onClick={handleSave}
                    disabled={!isDirty}
                />
                {isDirty && (
                    <Button
                        text="Discard Changes"
                        type="normal"
                        stylingMode="outlined"
                        onClick={handleDiscard}
                    />
                )}
                <Button
                    text="Restore Defaults"
                    type="normal"
                    stylingMode="outlined"
                    onClick={handleRestoreDefaults}
                />
                <Button
                    text="Clear All"
                    type="danger"
                    stylingMode="outlined"
                    onClick={handleClearAll}
                />
                {isDirty && (
                    <span style={{ color: "#f0ad4e", fontSize: "12px", marginLeft: "8px" }}>
                        Unsaved changes
                    </span>
                )}
            </div>

            <AddRowsDialog
                visible={addForGroup !== null}
                title={addForGroup === null ? "Add Transition" : `Add Transition - ${addForGroup}`}
                rowLabel="Transitions"
                rowItems={(groupedTransitions.find(g => g.label === addForGroup)?.options ?? [])
                    .filter(o => !rowsFor([o]).length)
                    .map(o => ({ id: rowKey(o), name: o.description ?? rowKey(o) }))}
                roleItems={groupedRoles}
                onCancel={() => setAddForGroup(null)}
                onAdd={(keys, roles) => addRows(addForGroup!, keys, roles)}
            />
        </div>
    );
}
